import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { WhatsApp } from '@mui/icons-material';
import { RootState } from '../../store';
import { CREAM_SOFT, INDIGO, INK, SANS, YELLOW, whatsappLink } from './publicTokens';

/**
 * Botón flotante de WhatsApp a la línea de ventas. Solo en páginas públicas
 * (landing, precios, promo y centro de ayuda), nunca dentro del panel.
 * No dispara eventos: el sitio no mide hoy ningún evento de contacto.
 */
const isPublicPath = (pathname: string) => {
  const p = pathname.replace(/\/+$/, '') || '/';
  return p === '/' || p === '/precios' || p === '/promo' || p === '/ayuda' || p.startsWith('/ayuda/');
};

export const WhatsAppFloat: React.FC = () => (
  <Box
    component="a"
    href={whatsappLink()}
    target="_blank"
    rel="noopener"
    aria-label="Hablá con nosotros por WhatsApp"
    sx={{
      position: 'fixed',
      right: { xs: 20, md: 28 },
      bottom: { xs: 'calc(20px + env(safe-area-inset-bottom))', md: 'calc(28px + env(safe-area-inset-bottom))' },
      zIndex: 1200,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.2,
      width: { xs: 56, md: 'auto' },
      height: 56,
      px: { xs: 0, md: 2.6 },
      borderRadius: 999,
      bgcolor: INK,
      color: CREAM_SOFT,
      border: `1.5px solid ${YELLOW}`,
      boxShadow: '0 10px 28px rgba(10,10,10,0.28)',
      textDecoration: 'none',
      fontFamily: SANS,
      fontWeight: 600,
      fontSize: '0.92rem',
      transition: 'transform 0.18s ease, background-color 0.18s ease',
      '&:hover': { transform: 'translateY(-2px)', bgcolor: INDIGO },
      '&:focus-visible': { outline: `3px solid ${YELLOW}`, outlineOffset: 3 },
      '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none', bgcolor: INDIGO } },
    }}
  >
    <WhatsApp sx={{ fontSize: 26, color: '#fff' }} />
    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
      Hablá con nosotros
    </Box>
  </Box>
);

/** Se monta una vez dentro del Router y decide por ruta. */
export const PublicWhatsAppFloat: React.FC = () => {
  const { pathname } = useLocation();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  if (!isPublicPath(pathname)) return null;
  // Con sesión, "/" redirige al panel: no mostrarlo en ese instante.
  if (isAuthenticated && pathname === '/') return null;
  return <WhatsAppFloat />;
};

export default PublicWhatsAppFloat;
